import React from 'react';
import {
  Box,
  Typography,
  Slider,
  Stack,
  Paper,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Info } from '@mui/icons-material';

interface PITargetRangeSelectorProps {
  values: {
    ideal_factor_a_min: number;
    ideal_factor_a_max: number;
    ideal_factor_b_min: number;
    ideal_factor_b_max: number;
    ideal_factor_c_min: number;
    ideal_factor_c_max: number;
    ideal_factor_d_min: number;
    ideal_factor_d_max: number;
    requires_a_greater_than_b?: boolean;
    requires_a_greater_than_c?: boolean;
    requires_a_greater_than_d?: boolean;
    requires_b_greater_than_a?: boolean;
    requires_b_greater_than_c?: boolean;
    requires_b_greater_than_d?: boolean;
    requires_c_greater_than_a?: boolean;
    requires_c_greater_than_b?: boolean;
    requires_c_greater_than_d?: boolean;
    requires_d_greater_than_a?: boolean;
    requires_d_greater_than_b?: boolean;
    requires_d_greater_than_c?: boolean;
  };
  onChange: (field: string, value: any) => void;
}

const factorInfo = {
  A: {
    name: 'Dominance',
    low: 'Collaborative',
    high: 'Independent',
    description: 'Measures the drive to exert influence on people or events',
  },
  B: {
    name: 'Extraversion',
    low: 'Reserved',
    high: 'Sociable',
    description: 'Measures the drive for social interaction with other people',
  },
  C: {
    name: 'Patience',
    low: 'Driving',
    high: 'Steady',
    description: 'Measures the drive for consistency and stability',
  },
  D: {
    name: 'Formality',
    low: 'Flexible',
    high: 'Precise',
    description: 'Measures the drive to conform to rules and structure',
  },
};

// Convert between 0-100 scale and sigma scale (-3 to +3)
const toSigma = (value: number): number => {
  return (value - 50) / 16.67; // 50 = 0σ, each σ ≈ 16.67 units
};

const fromSigma = (sigma: number): number => {
  return Math.round(sigma * 16.67 + 50);
};

const PITargetRangeSelector: React.FC<PITargetRangeSelectorProps> = ({
  values,
  onChange,
}) => {
  const SIGMA_WIDTH = 16.67; // 1 sigma in our 0-100 scale

  // Calculate center values for each factor
  const getCenterValue = (factor: string): number => {
    const min = values[`ideal_factor_${factor.toLowerCase()}_min` as keyof typeof values] as number || 0;
    const max = values[`ideal_factor_${factor.toLowerCase()}_max` as keyof typeof values] as number || 100;
    return (min + max) / 2;
  };

  // Update factor relationships based on current values
  const updateFactorRelationships = () => {
    const factors = ['a', 'b', 'c', 'd'];
    const centers: Record<string, number> = {};
    
    // Get all center values in sigma scale
    factors.forEach(f => {
      centers[f] = toSigma(getCenterValue(f));
    });

    // Check each pair of factors
    const pairs = [
      ['a', 'b'], ['a', 'c'], ['a', 'd'],
      ['b', 'c'], ['b', 'd'], ['c', 'd']
    ];

    pairs.forEach(([f1, f2]) => {
      const diff = centers[f1] - centers[f2];
      
      // Only update if difference is >= 0.5 sigma
      if (Math.abs(diff) >= 0.5) {
        if (diff > 0) {
          // f1 > f2
          onChange(`requires_${f1}_greater_than_${f2}`, true);
          onChange(`requires_${f2}_greater_than_${f1}`, false);
        } else {
          // f2 > f1
          onChange(`requires_${f2}_greater_than_${f1}`, true);
          onChange(`requires_${f1}_greater_than_${f2}`, false);
        }
      } else {
        // Difference is less than 0.5 sigma, clear both relationships
        onChange(`requires_${f1}_greater_than_${f2}`, false);
        onChange(`requires_${f2}_greater_than_${f1}`, false);
      }
    });
  };

  const handleCenterChange = (factor: string, sigmaCenter: number) => {
    // Convert sigma to 0-100 scale
    const center = fromSigma(sigmaCenter);
    
    // Set min and max to be ±0.5 sigma from center (total 1 sigma width)
    const min = Math.max(0, Math.min(100, center - SIGMA_WIDTH / 2));
    const max = Math.max(0, Math.min(100, center + SIGMA_WIDTH / 2));
    
    onChange(`ideal_factor_${factor.toLowerCase()}_min`, min);
    onChange(`ideal_factor_${factor.toLowerCase()}_max`, max);

    // Update factor relationships after a short delay to ensure state is updated
    setTimeout(() => {
      updateFactorRelationships();
    }, 0);
  };

  const renderFactorSlider = (factor: 'A' | 'B' | 'C' | 'D') => {
    const info = factorInfo[factor];
    const min = values[`ideal_factor_${factor.toLowerCase()}_min` as keyof typeof values] as number || 0;
    const max = values[`ideal_factor_${factor.toLowerCase()}_max` as keyof typeof values] as number || 100;
    const center = (min + max) / 2;
    const sigmaCenter = toSigma(center);

    return (
      <Box key={factor} sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            Factor {factor} - {info.name}
          </Typography>
          <Tooltip title={info.description}>
            <IconButton size="small">
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ px: 1, mr: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              {info.low}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {info.high}
            </Typography>
          </Stack>
          
          <Box sx={{ position: 'relative', height: 60, mr: 1 }}>
            {/* Sigma scale labels */}
            <Box sx={{ 
              position: 'absolute', 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'space-between',
              bottom: 15,
              px: 0.5
            }}>
              {[-3, -2, -1, 0, 1, 2, 3].map(sigma => (
                <Typography 
                  key={sigma} 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    left: `${(sigma + 3) * 16.67}%`,
                    transform: 'translateX(-50%)',
                    fontWeight: sigma === 0 ? 'bold' : 'normal'
                  }}
                >
                  {sigma > 0 ? `+${sigma}σ` : `${sigma}σ`}
                </Typography>
              ))}
            </Box>
            
            {/* Range visualization - just a thick line on the rail */}
            <Box sx={{ 
              position: 'absolute',
              top: 28,
              left: `${min}%`,
              width: `${max - min}%`,
              height: 4,
              backgroundColor: 'primary.main',
              borderRadius: 2,
              zIndex: 2
            }} />
            
            {/* Slider */}
            <Slider
              value={sigmaCenter}
              onChange={(_, newValue) => handleCenterChange(factor, newValue as number)}
              min={-3}
              max={3}
              step={0.1}
              marks={[
                { value: -3, label: '' },
                { value: -2, label: '' },
                { value: -1, label: '' },
                { value: 0, label: '' },
                { value: 1, label: '' },
                { value: 2, label: '' },
                { value: 3, label: '' },
              ]}
              sx={{
                position: 'absolute',
                top: 20,
                width: 'calc(100% - 12px)',
                left: 6,
                right: 6,
                '& .MuiSlider-track': {
                  display: 'none'
                },
                '& .MuiSlider-rail': {
                  height: 2,
                  opacity: 1,
                  backgroundColor: '#e0e0e0'
                },
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                  backgroundColor: '#fff',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  zIndex: 3,
                  '&:hover': {
                    boxShadow: 'none'
                  },
                  '&.Mui-focusVisible': {
                    boxShadow: 'none'
                  },
                  '&::before': {
                    display: 'none'
                  },
                  '&::after': {
                    display: 'none'
                  }
                },
                '& .MuiSlider-valueLabel': {
                  display: 'none'
                },
                '& .MuiSlider-mark': {
                  height: 8,
                  width: 1,
                  backgroundColor: '#bdbdbd',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }
              }}
            />
          </Box>
          
          <Typography variant="body2" align="center" color="primary" sx={{ mt: 0.5 }}>
            Target range: {toSigma(min).toFixed(1)}σ to {toSigma(max).toFixed(1)}σ
          </Typography>
        </Box>
        
        {/* Number inputs for testing and precise control */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">Min:</Typography>
            <input
              type="number"
              data-testid={`factor-${factor.toLowerCase()}-min`}
              value={Math.round(min)}
              onChange={(e) => {
                const newMin = parseInt(e.target.value) || 0;
                onChange(`ideal_factor_${factor.toLowerCase()}_min`, newMin);
              }}
              style={{
                width: '60px',
                padding: '4px',
                fontSize: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
              min="0"
              max="100"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">Max:</Typography>
            <input
              type="number"
              data-testid={`factor-${factor.toLowerCase()}-max`}
              value={Math.round(max)}
              onChange={(e) => {
                const newMax = parseInt(e.target.value) || 0;
                onChange(`ideal_factor_${factor.toLowerCase()}_max`, newMax);
              }}
              style={{
                width: '60px',
                padding: '4px',
                fontSize: '12px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
              min="0"
              max="100"
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, overflow: 'hidden' }}>
      <Typography variant="h6" gutterBottom>
        PI Behavioral Target Ranges
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Set the ideal PI factor ranges for candidates. Each range covers 1 sigma (σ) width.
        The center position (0σ) represents the average. Drag the slider to position the target range.
      </Typography>

      <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
        {['A', 'B', 'C', 'D'].map((factor) => 
          renderFactorSlider(factor as 'A' | 'B' | 'C' | 'D')
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Factor Relationship Requirements
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Select the required relationships between factors. Relationships are automatically set when factors are ≥0.5σ apart.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
          {/* A vs B */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor A vs Factor B
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('A')) - toSigma(getCenterValue('B'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_a_greater_than_b || false}
                    onChange={(e) => {
                      onChange('requires_a_greater_than_b', e.target.checked);
                      if (e.target.checked) onChange('requires_b_greater_than_a', false);
                    }}
                  />
                }
                label="A > B (More Independent than Sociable)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_b_greater_than_a || false}
                    onChange={(e) => {
                      onChange('requires_b_greater_than_a', e.target.checked);
                      if (e.target.checked) onChange('requires_a_greater_than_b', false);
                    }}
                  />
                }
                label="B > A (More Sociable than Independent)"
              />
            </Stack>
          </Paper>

          {/* A vs C */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor A vs Factor C
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('A')) - toSigma(getCenterValue('C'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_a_greater_than_c || false}
                    onChange={(e) => {
                      onChange('requires_a_greater_than_c', e.target.checked);
                      if (e.target.checked) onChange('requires_c_greater_than_a', false);
                    }}
                  />
                }
                label="A > C (More Independent than Steady)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_c_greater_than_a || false}
                    onChange={(e) => {
                      onChange('requires_c_greater_than_a', e.target.checked);
                      if (e.target.checked) onChange('requires_a_greater_than_c', false);
                    }}
                  />
                }
                label="C > A (More Steady than Independent)"
              />
            </Stack>
          </Paper>

          {/* A vs D */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor A vs Factor D
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('A')) - toSigma(getCenterValue('D'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_a_greater_than_d || false}
                    onChange={(e) => {
                      onChange('requires_a_greater_than_d', e.target.checked);
                      if (e.target.checked) onChange('requires_d_greater_than_a', false);
                    }}
                  />
                }
                label="A > D (More Independent than Precise)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_d_greater_than_a || false}
                    onChange={(e) => {
                      onChange('requires_d_greater_than_a', e.target.checked);
                      if (e.target.checked) onChange('requires_a_greater_than_d', false);
                    }}
                  />
                }
                label="D > A (More Precise than Independent)"
              />
            </Stack>
          </Paper>

          {/* B vs C */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor B vs Factor C
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('B')) - toSigma(getCenterValue('C'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_b_greater_than_c || false}
                    onChange={(e) => {
                      onChange('requires_b_greater_than_c', e.target.checked);
                      if (e.target.checked) onChange('requires_c_greater_than_b', false);
                    }}
                  />
                }
                label="B > C (More Sociable than Steady)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_c_greater_than_b || false}
                    onChange={(e) => {
                      onChange('requires_c_greater_than_b', e.target.checked);
                      if (e.target.checked) onChange('requires_b_greater_than_c', false);
                    }}
                  />
                }
                label="C > B (More Steady than Sociable)"
              />
            </Stack>
          </Paper>

          {/* B vs D */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor B vs Factor D
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('B')) - toSigma(getCenterValue('D'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_b_greater_than_d || false}
                    onChange={(e) => {
                      onChange('requires_b_greater_than_d', e.target.checked);
                      if (e.target.checked) onChange('requires_d_greater_than_b', false);
                    }}
                  />
                }
                label="B > D (More Sociable than Precise)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_d_greater_than_b || false}
                    onChange={(e) => {
                      onChange('requires_d_greater_than_b', e.target.checked);
                      if (e.target.checked) onChange('requires_b_greater_than_d', false);
                    }}
                  />
                }
                label="D > B (More Precise than Sociable)"
              />
            </Stack>
          </Paper>

          {/* C vs D */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold">
                Factor C vs Factor D
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Δ = {Math.abs(toSigma(getCenterValue('C')) - toSigma(getCenterValue('D'))).toFixed(1)}σ
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_c_greater_than_d || false}
                    onChange={(e) => {
                      onChange('requires_c_greater_than_d', e.target.checked);
                      if (e.target.checked) onChange('requires_d_greater_than_c', false);
                    }}
                  />
                }
                label="C > D (More Steady than Precise)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.requires_d_greater_than_c || false}
                    onChange={(e) => {
                      onChange('requires_d_greater_than_c', e.target.checked);
                      if (e.target.checked) onChange('requires_c_greater_than_d', false);
                    }}
                  />
                }
                label="D > C (More Precise than Steady)"
              />
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Paper>
  );
};

export default PITargetRangeSelector;